import jsPDF from 'jspdf';
import { pipe } from 'remeda';
import { match } from 'ts-pattern';

import Progress from '@core/app/actions/progress-caller';
import { LayerModule } from '@core/app/constants/layer-module/layer-modules';
import currentFileManager from '@core/app/svgedit/currentFileManager';
import layerManager from '@core/app/svgedit/layer/layerManager';
import selectionManager from '@core/app/svgedit/selection';
import workareaManager from '@core/app/svgedit/workarea';
import { updateRecentFiles } from '@core/helpers/file/recentFiles';
import i18n from '@core/helpers/i18n';
import svgStringToCanvas from '@core/helpers/image/svgStringToCanvas';
import { buildWebFontFaceCss } from '@core/helpers/image/webFontFaceCss';
import { getData } from '@core/helpers/layer/layer-config-helper';
import { layersToA4Base64 } from '@core/helpers/layer/layersToA4Base64';
import { convertAllTextToPath } from '@core/helpers/path/convertToPath';
import { getSVGAsync } from '@core/helpers/svg-editor-helper';
import { isMac } from '@core/helpers/system-helper';
import { convertVariableText } from '@core/helpers/variableText';
import dialog from '@core/implementations/dialog';
import type ISVGCanvas from '@core/interfaces/ISVGCanvas';

import { getDefaultFileName, switchSymbolWrapper } from '../utils/common';
import { checkNounProjectElements, removeNPElementsWrapper } from '../utils/nounProject';

let svgCanvas: ISVGCanvas;

getSVGAsync((globalSVG) => {
  svgCanvas = globalSVG.Canvas;
});

export const exportAsBVG = async (): Promise<boolean> => {
  if (!(await checkNounProjectElements())) {
    return false;
  }

  selectionManager.clearSelection();

  const defaultFileName = getDefaultFileName();
  const langFile = i18n.lang.topmenu.file;

  svgCanvas.removeUnusedDefs();

  const getContent = () =>
    removeNPElementsWrapper(() =>
      switchSymbolWrapper(async () => {
        const revert = await convertVariableText();

        try {
          return svgCanvas.getSvgString();
        } finally {
          revert?.();
        }
      }),
    );
  const newFilePath = await dialog.writeFileDialog(getContent, langFile.save_scene, defaultFileName, [
    { extensions: ['bvg'], name: isMac() ? `${langFile.scene_files} (*.bvg)` : langFile.scene_files },
    { extensions: ['*'], name: langFile.all_files },
  ]);

  if (newFilePath) {
    currentFileManager.setLocalFile(newFilePath);
    updateRecentFiles(newFilePath);
    currentFileManager.setHasUnsavedChanges(false, false);

    return true;
  }

  return false;
};

export const exportAsSVG = async (): Promise<void> => {
  if (!(await checkNounProjectElements())) {
    return;
  }

  selectionManager.clearSelection();
  svgCanvas.removeUnusedDefs();

  const getContent = async () => {
    const reverts: Array<(() => void) | null | undefined> = [];
    const layers = Array.from(document.querySelectorAll<SVGGElement>('#svgcontent g.layer'));
    const clipPaths = layers.map((layer) => layer.getAttribute('clip-path'));

    try {
      reverts.push(await convertVariableText(), (await convertAllTextToPath()).revert);
      layers.forEach((layer) => layer.removeAttribute('clip-path'));

      return await removeNPElementsWrapper(() => switchSymbolWrapper(() => svgCanvas.getSvgString({ unit: 'mm' })));
    } finally {
      layers.forEach((layer, index) => {
        const clipPath = clipPaths[index];

        if (clipPath === null) layer.removeAttribute('clip-path');
        else layer.setAttribute('clip-path', clipPath);
      });
      reverts.toReversed().forEach((revert) => revert?.());
    }
  };
  const defaultFileName = getDefaultFileName();
  const langFile = i18n.lang.topmenu.file;

  await dialog.writeFileDialog(getContent, langFile.save_svg, defaultFileName, [
    { extensions: ['svg'], name: isMac() ? `${langFile.svg_files} (*.svg)` : langFile.svg_files },
    { extensions: ['*'], name: langFile.all_files },
  ]);
};

export const exportAsImage = async (type: 'jpg' | 'png'): Promise<void> => {
  const langFile = i18n.lang.topmenu.file;

  selectionManager.clearSelection();
  svgCanvas.removeUnusedDefs();

  const getContent = async () => {
    const revert = await convertVariableText();
    let output: string;

    try {
      // the isolated <img> render cannot see the app document's webfonts, so inline their bytes
      // instead of converting text to paths
      const fontFaceCss = await buildWebFontFaceCss([document.getElementById('svgcontent')!]);

      output = (await switchSymbolWrapper(() => svgCanvas.getSvgString())).replace(
        /<svg[^>]*>/,
        (svgTag) => svgTag + fontFaceCss,
      );
    } finally {
      revert?.();
    }

    Progress.openNonstopProgress({ id: 'export_image', message: langFile.converting });

    const { height, width } = workareaManager;
    const canvas = await svgStringToCanvas(output, width, height);
    const base64 = match(type)
      .with('png', () =>
        pipe(canvas.toDataURL('image/png'), (base64) => base64.replace(/^data:image\/\w+;base64,/, '')),
      )
      .with('jpg', () => {
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);

        return pipe(canvas.toDataURL('image/jpeg', 1.0), (base64) => base64.replace(/^data:image\/\w+;base64,/, ''));
      })
      .exhaustive();

    return new Blob([Buffer.from(base64, 'base64') as unknown as Blob]);
  };
  const defaultFileName = getDefaultFileName();
  const fileTypeName = `${langFile[`${type}_files`]}`;

  try {
    await dialog.writeFileDialog(getContent, langFile[`save_${type}`], defaultFileName, [
      { extensions: [type], name: isMac() ? `${fileTypeName} (*.${type})` : fileTypeName },
      { extensions: ['*'], name: langFile.all_files },
    ]);
  } finally {
    Progress.popById('export_image');
  }
};

export const exportUvPrintAsPdf = async (): Promise<void> => {
  selectionManager.clearSelection();
  svgCanvas.removeUnusedDefs();

  const {
    topmenu: { file: lang },
  } = i18n.lang;
  const revert = await convertVariableText();
  let base64: string;

  try {
    const layers = layerManager
      .getAllLayers()
      .map((layer) => layer.getGroup())
      .filter((layerG) => getData(layerG, 'module') === LayerModule.UV_PRINT);

    base64 = await switchSymbolWrapper(() => layersToA4Base64(layers));
  } finally {
    revert?.();
  }

  const defaultFileName = getDefaultFileName();

  const pdf = new jsPDF().addImage(base64, 'PNG', 0, 0, 210, 297);
  const getContent = () => new Blob([pdf.output('blob')], { type: 'application/pdf' });

  await dialog.writeFileDialog(getContent, lang.save_pdf, defaultFileName, [
    { extensions: ['pdf'], name: isMac() ? `PDF (*.pdf)` : 'PDF' },
    { extensions: ['*'], name: lang.all_files },
  ]);
};
